import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Box,
  Typography,
  Divider,
  alpha,
  useTheme,
  Avatar,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  Add as AddIcon,
  Close as CloseIcon,
  ListAlt as ListAltIcon,
} from "@mui/icons-material";
import { useMutation } from "react-query";
import { enqueueSnackbar } from "notistack";
import { createChecklist } from "../../../api/services/settings";
import CustomInput from "../../FormFields/CustomInput";

const CreateChecklist = ({ onFormAdded, refetch, show, handleClose }) => {
  const theme = useTheme();
  const [name, setName] = useState("");
  const [instructions, setInstructions] = useState([{ text: "" }]);

  const postMutation = useMutation(createChecklist, {
    onSuccess: () => {
      enqueueSnackbar("New Checklist added successfully", { variant: "success" });
      resetForm();
      refetch();
      if (onFormAdded) onFormAdded();
      handleClose();
    },
    onError: () => {
      enqueueSnackbar("Failed to add Checklist", { variant: "error" });
    },
  });

  const resetForm = () => {
    setName("");
    setInstructions([{ text: "" }]);
  };

  const handleAddInstruction = () => {
    setInstructions([...instructions, { text: "" }]);
  };

  const handleRemoveInstruction = (index) => {
    const updated = instructions.filter((_, i) => i !== index);
    setInstructions(updated.length ? updated : [{ text: "" }]);
  };

  const handleInstructionChange = (index, value) => {
    const updated = [...instructions];
    updated[index].text = value;
    setInstructions(updated);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const payload = {
      name: name,
      instructions: instructions
        .filter((inst) => inst.text.trim() !== "")
        .map((inst) => ({
          text: inst.text,
          is_required: true,
          sub_instructions: [],
        })),
    };

    postMutation.mutate(payload);
  };

  return (
    <Dialog
      open={show}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: "16px",
          boxShadow: "0px 24px 48px -12px rgba(16, 24, 40, 0.25)",
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle sx={{ p: 0 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 2.5,
            pb: 1.5,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Avatar
              sx={{
                width: 40,
                height: 40,
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                color: theme.palette.primary.main,
              }}
            >
              <ListAltIcon sx={{ fontSize: 20 }} />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight={600} sx={{ color: "#1A1A2E" }}>
                Add New Checklist
              </Typography>
              <Typography variant="caption" sx={{ color: "#6B7280" }}>
                Create a new checklist with instructions
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={handleClose}
            size="small"
            sx={{
              color: "#9CA3AF",
              "&:hover": { bgcolor: "#F3F4F6", color: "#4B5563" },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider sx={{ opacity: 0.4 }} />
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent sx={{ p: 2.5 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
            <CustomInput
              label="Category Name"
              fullWidth
              placeholder="e.g. Morning Safety Routine"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />

            <Divider>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                INSTRUCTIONS
              </Typography>
            </Divider>

            {instructions.map((inst, index) => (
              <Box
                key={index}
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1,
                  bgcolor: "#F8FAFC",
                  p: 1.5,
                  borderRadius: 2,
                  border: "1px solid #E2E8F0",
                }}
              >
                <CustomInput
                  label={`Task #${index + 1}`}
                  fullWidth
                  value={inst.text}
                  onChange={(e) => handleInstructionChange(index, e.target.value)}
                  placeholder="Enter instruction..."
                />
                <IconButton
                  color="error"
                  onClick={() => handleRemoveInstruction(index)}
                  disabled={instructions.length === 1 && !inst.text}
                  sx={{ mt: 1 }}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            ))}

            <Button
              startIcon={<AddIcon />}
              onClick={handleAddInstruction}
              sx={{
                alignSelf: "flex-start",
                textTransform: "none",
                fontWeight: 600,
                color: "#2469F0",
                "&:hover": {
                  bgcolor: alpha("#2469F0", 0.04),
                },
              }}
            >
              Add More Instruction
            </Button>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 2.5, pt: 0, gap: 1.5 }}>
          <Button
            onClick={handleClose}
            variant="outlined"
            sx={{
              textTransform: "none",
              fontWeight: 500,
              borderRadius: "10px",
              borderColor: "#D0D5DD",
              color: "#344054",
              "&:hover": {
                bgcolor: "#F9FAFB",
                borderColor: "#98A2B3",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={!name.trim() || postMutation.isLoading}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "10px",
              bgcolor: "#2469F0",
              px: 4,
              "&:hover": {
                bgcolor: "#1A5BD4",
                boxShadow: "0px 4px 12px rgba(36, 105, 240, 0.3)",
              },
            }}
          >
            {postMutation.isLoading ? "Saving..." : "Create Checklist"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default CreateChecklist;